import { type FC, useMemo } from 'react'
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, dehydrate, QueryClient } from '@tanstack/react-query'

import LessonPage, { type LessonProps } from 'views/GrowthMasters/LessonPage'
import RedirectToHome from 'views/GrowthMasters/components/RedirectToHome'
import useInitialCookie from 'views/GrowthMasters/hooks/useInitialCookie'

import { GatedContentProvider } from 'contexts/gatedContent'

import { fetchLessonsSlugs, lessonQuery } from '@segment/api/routes/growthMasters/queries'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

const GrowthMastersLessonPage: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ slug }) => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])
  const [showContent, isLoading, updateRegistered] = useInitialCookie()

  return showContent ? (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <GatedContentProvider value={{ showContent, updateRegistered }}>
        {isLoading ? null : <LessonPage slug={slug} />}
      </GatedContentProvider>
    </StaticSiteBackendProvider>
  ) : isLoading ? null : (
    <RedirectToHome />
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const staticSiteBackend = createStaticSiteBackend()
  const slugs = await fetchLessonsSlugs(staticSiteBackend)

  const nonEmptySlugs = slugs.filter((entry) => entry.slug && entry.slug.length > 0)
  return {
    paths: nonEmptySlugs.map((entry) => ({ params: { slug: entry.slug } })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & LessonProps> = async ({ params }) => {
  const queryClient = new QueryClient()
  const staticSiteBackend = createStaticSiteBackend()

  let slug: string | null = null

  if (params?.slug) {
    slug = params.slug as string
  }

  if (!slug)
    throw new Error(
      `Missing slug route parameter. Make sure that all slug values returned from getStaticPaths are truthy`
    )

  await queryClient.prefetchQuery(...lessonQuery(staticSiteBackend, slug), {
    staleTime: Infinity,
  })

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & { slug: string } = {
    dehydratedState: safeDehydratedState,
    slug,
  }

  return {
    props,
  }
}

export default GrowthMastersLessonPage
